import React, { useState, useRef } from 'react';
import { Download, Upload, FileText, Play, Plus, X, UserPlus } from 'lucide-react';
import { GoogleSheetsService } from '../services/GoogleSheetsService';

const PlayerSetup = ({ 
  players, 
  sessionType,
  onSessionTypeChange,
  onImportPlayers, 
  onFileUpload, 
  onStartSession 
}) => {
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [competitionLevel, setCompetitionLevel] = useState('competitive');
  const [courtCount, setCourtCount] = useState(3);
  const [expectedPlayers, setExpectedPlayers] = useState(23);
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [newPlayer, setNewPlayer] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    rating: 3.5,
    age: ''
  });
  const fileInputRef = useRef(null);
  
  const gameDuration = 12;

  // Handle loading sample data
  const handleLoadSampleData = () => {
    try {
      setImportError('');
      const sampleData = GoogleSheetsService.generateSampleData();
      console.log('Loading sample data:', sampleData);
      onImportPlayers(sampleData);
      
      // Auto-select players based on expected count for SMART mode
      if (sessionType !== 'mlp') {
        const playersToSelect = sampleData.slice(0, expectedPlayers);
        setSelectedPlayers(playersToSelect);
      }
    } catch (error) {
      setImportError('Failed to load sample data: ' + error.message);
    }
  };

  // Handle Google Sheets import
  const handleGoogleSheetsImport = async () => {
    if (!googleSheetsUrl.trim()) {
      setImportError('Please enter a Google Sheets URL');
      return;
    }

    setIsImporting(true);
    setImportError('');

    try {
      const csvUrl = GoogleSheetsService.convertToCSVUrl(googleSheetsUrl);
      const response = await fetch(csvUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }
      
      const csvText = await response.text();
      const players = GoogleSheetsService.parseCSV(csvText);
      
      if (players.length === 0) {
        throw new Error('No valid players found in the sheet');
      }
      
      console.log('Imported players from Google Sheets:', players);
      onImportPlayers(players);
      setGoogleSheetsUrl('');
      
      // Auto-select players based on expected count for SMART mode
      if (sessionType !== 'mlp') {
        const playersToSelect = players.slice(0, expectedPlayers);
        setSelectedPlayers(playersToSelect);
      }
    } catch (error) {
      setImportError('Import failed: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  // Handle CSV file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportError('');
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const players = GoogleSheetsService.parseCSV(csvText);
        
        if (players.length === 0) {
          throw new Error('No valid players found in the CSV file');
        }
        
        console.log('Imported players from CSV:', players);
        onImportPlayers(players);
        
        // Auto-select players based on expected count for SMART mode
        if (sessionType !== 'mlp') {
          const playersToSelect = players.slice(0, expectedPlayers);
          setSelectedPlayers(playersToSelect);
        }
      } catch (error) {
        setImportError('CSV import failed: ' + error.message);
      }
    };
    
    reader.onerror = () => {
      setImportError('Failed to read file');
    };
    
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  // Handle manual player addition
  const handleAddManualPlayer = () => {
    if (!newPlayer.firstName.trim() || !newPlayer.lastName.trim()) {
      setImportError('First name and last name are required');
      return;
    }

    const player = {
      id: Date.now() + Math.random(),
      firstName: newPlayer.firstName.trim(),
      lastName: newPlayer.lastName.trim(),
      name: `${newPlayer.firstName.trim()} ${newPlayer.lastName.trim()}`,
      gender: newPlayer.gender || 'Not specified',
      age: newPlayer.age ? parseInt(newPlayer.age) : null,
      rating: parseFloat(newPlayer.rating) || 3.5,
      totalGames: 0,
      sessionsEligible: 'All',
      lastSession: new Date().toISOString().split('T')[0]
    };

    const updatedPlayers = [...players, player];
    onImportPlayers(updatedPlayers);
    
    // Auto-select for SMART mode
    if (sessionType !== 'mlp') {
      setSelectedPlayers([...selectedPlayers, player]);
    }

    // Reset form
    setNewPlayer({
      firstName: '',
      lastName: '',
      gender: '',
      rating: 3.5,
      age: ''
    });
    setShowManualAdd(false);
    setImportError('');
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    const templateData = [
      ['First Name', 'Last Name', 'Gender', 'Age', 'Rating', 'Total Games', 'Sessions Eligible', 'Last Played'],
      ['John', 'Smith', 'Male', '32', '3.8', '25', 'All', '2024-01-15'],
      ['Jane', 'Doe', 'Female', '28', '4.1', '18', 'All', '2024-01-14'],
      ['Mike', 'Johnson', 'Male', '35', '3.5', '32', 'All', '2024-01-13']
    ];
    
    const csvContent = templateData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pickleball_players_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handlePlayerToggle = (player) => {
    setSelectedPlayers(prev => {
      const isSelected = prev.find(p => p.id === player.id);
      if (isSelected) {
        return prev.filter(p => p.id !== player.id);
      } else {
        // Check if we're at the expected player limit for SMART mode
        if (sessionType !== 'mlp' && prev.length >= expectedPlayers) {
          setImportError(`Cannot select more than ${expectedPlayers} players for this session`);
          return prev;
        }
        setImportError(''); // Clear any previous error
        return [...prev, player];
      }
    });
  };

  const handleSelectAll = () => {
    if (players && Array.isArray(players)) {
      if (sessionType !== 'mlp' && players.length > expectedPlayers) {
        // Only select up to the expected player count for SMART mode
        setSelectedPlayers(players.slice(0, expectedPlayers));
        setImportError(`Limited selection to ${expectedPlayers} players as requested`);
      } else {
        setSelectedPlayers([...players]);
        setImportError('');
      }
    }
  };

  const handleSelectNone = () => {
    setSelectedPlayers([]);
    setImportError(''); // Clear any error when deselecting
  };

  const handleStartSession = () => {
    const sessionConfig = {
      competitionLevel,
      courtCount,
      gameDuration,
      sessionType
    };
    
    onStartSession(selectedPlayers, sessionConfig);
  };

  // Get player count and waiting count
  const getPlayerCounts = () => {
    if (sessionType === 'mlp') {
      return {
        waitingCount: Math.max(0, players.length - 12)
      };
    } else {
      return {
        waitingCount: Math.max(0, expectedPlayers - (courtCount * 4))
      };
    }
  };

  const { waitingCount } = getPlayerCounts();
  const isMLPMode = sessionType === 'mlp';
  
  // Fixed button enable logic
  const hasEnoughPlayers = isMLPMode 
    ? players.length >= 16  // MLP needs exactly 16 loaded players
    : selectedPlayers.length >= 8;  // SMART needs at least 8 selected players

  return (
    <div className="player-setup">
      <div className="setup-header">
        <h2>Setup New Session</h2>
        <p>Import players and configure your pickleball session</p>
      </div>

      {/* Import Players Section */}
      <div className="import-section">
        <h3>Import Players</h3>
        
        {/* Google Sheets Import */}
        <div className="google-sheets-import">
          <div className="sheets-input-group">
            <input
              type="text"
              placeholder="Paste Google Sheets URL here..."
              className="sheets-input"
              value={googleSheetsUrl}
              onChange={(e) => setGoogleSheetsUrl(e.target.value)}
              disabled={isImporting}
            />
            <button
              className="sheets-submit-button"
              onClick={handleGoogleSheetsImport}
              disabled={isImporting || !googleSheetsUrl.trim()}
            >
              {isImporting ? 'Importing...' : 'Import'}
            </button>
          </div>
          <p className="sheets-help">
            Share your Google Sheet publicly ("Anyone with the link can view") and paste the link above
          </p>
        </div>
        
        {/* Import Buttons */}
        <div className="import-buttons">
          <button 
            className="import-button primary" 
            onClick={handleLoadSampleData}
          >
            <Download size={16} />
            Load Sample Data
          </button>
          <button 
            className="import-button secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={16} />
            Upload CSV File
          </button>
          <button 
            className="import-button secondary"
            onClick={handleDownloadTemplate}
          >
            <FileText size={16} />
            Download Template
          </button>
          <button 
            className="import-button secondary"
            onClick={() => setShowManualAdd(!showManualAdd)}
          >
            <UserPlus size={16} />
            Add Player Manually
          </button>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".csv"
          style={{ display: 'none' }}
        />

        {/* Error Display */}
        {importError && (
          <div className="import-error">
            {importError}
          </div>
        )}

        {/* Manual Player Addition Form */}
        {showManualAdd && (
          <div className="manual-add-form">
            <div className="form-header">
              <h4>Add New Player</h4>
              <button 
                className="close-button"
                onClick={() => setShowManualAdd(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="form-grid">
              <input
                type="text"
                placeholder="First Name *"
                value={newPlayer.firstName}
                onChange={(e) => setNewPlayer({...newPlayer, firstName: e.target.value})}
                className="form-input"
              />
              <input
                type="text"
                placeholder="Last Name *"
                value={newPlayer.lastName}
                onChange={(e) => setNewPlayer({...newPlayer, lastName: e.target.value})}
                className="form-input"
              />
              <select
                value={newPlayer.gender}
                onChange={(e) => setNewPlayer({...newPlayer, gender: e.target.value})}
                className="form-input"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              <input
                type="number"
                placeholder="Age"
                value={newPlayer.age}
                onChange={(e) => setNewPlayer({...newPlayer, age: e.target.value})}
                className="form-input"
                min="1"
                max="100"
              />
              <input
                type="number"
                placeholder="Rating (1.0-6.0)"
                value={newPlayer.rating}
                onChange={(e) => setNewPlayer({...newPlayer, rating: e.target.value})}
                className="form-input"
                min="1.0"
                max="6.0"
                step="0.1"
              />
            </div>
            <div className="form-actions">
              <button
                className="add-player-button"
                onClick={handleAddManualPlayer}
                disabled={!newPlayer.firstName.trim() || !newPlayer.lastName.trim()}
              >
                <Plus size={16} />
                Add Player
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Players List Status */}
      {players && players.length > 0 && (
        <div className="players-status">
          <h4>Players Loaded: {players.length}</h4>
          <div className="players-preview">
            {players.slice(0, 3).map(player => (
              <span key={player.id} className="player-preview">
                {player.name} ({player.rating})
              </span>
            ))}
            {players.length > 3 && <span className="more-players">+{players.length - 3} more</span>}
          </div>
        </div>
      )}

      {/* Dynamic Session Configuration */}
      <div className="config-section">
        <h3>Dynamic Session Configuration</h3>
        
        <div className="config-grid">
          <div className="config-item">
            <label>Number of Courts:</label>
            <select 
              value={courtCount} 
              onChange={(e) => setCourtCount(parseInt(e.target.value))}
              disabled={isMLPMode}
            >
              {[1,2,3,4,5,6].map(num => (
                <option key={num} value={num}>{num} Courts</option>
              ))}
            </select>
            <p className="config-note">
              {isMLPMode 
                ? "Fixed at 3 courts for MLP tournament"
                : `Sets player limits: ${courtCount * 4}-${courtCount * 6} (optimal: ${Math.floor(courtCount * 5)})`
              }
            </p>
          </div>

          <div className="config-item">
            <label>Number of Players:</label>
            {isMLPMode ? (
              <div className="player-count-display">
                {players.length}
              </div>
            ) : (
              <input
                type="number"
                min="8"
                max="24"
                value={expectedPlayers}
                onChange={(e) => setExpectedPlayers(parseInt(e.target.value) || 23)}
                className="sheets-input"
                style={{ textAlign: 'center', fontSize: '18px', fontWeight: 'bold' }}
              />
            )}
            <p className="config-note">
              {isMLPMode 
                ? `MLP Range: 16 | Available: ${players.length}`
                : `SMART Range: ${courtCount * 4}-24 | Available: ${selectedPlayers.length}`
              }
            </p>
          </div>

          <div className="config-item">
            <label>Competition Intensity:</label>
            <select 
              value={competitionLevel} 
              onChange={(e) => setCompetitionLevel(e.target.value)}
              disabled={isMLPMode}
            >
              <option value="recreational">Recreational</option>
              <option value="competitive">Competitive</option>
              <option value="advanced">Advanced</option>
            </select>
            <p className="config-note">
              {isMLPMode ? "Tournament level competition" : "Standard league play"}
            </p>
          </div>

          <div className="config-item">
            <label>Session Type:</label>
            <select 
              value={sessionType} 
              onChange={(e) => onSessionTypeChange(e.target.value)}
            >
              <option value="smart">SMART Open Play</option>
              <option value="mlp">MLP Tournament</option>
            </select>
            <p className="config-note">
              {sessionType === 'smart' 
                ? "Adaptive wait distribution (4C+5 to 8C), partnership optimization, DINKR V2.0 ratings"
                : "Round robin tournament format with 24 predetermined games"
              }
            </p>
          </div>
        </div>

        {/* Session Summary */}
        <div className="session-summary">
          <div className="summary-item">
            <span className="summary-value">{courtCount} courts</span>
            <span className="summary-label">Courts:</span>
          </div>
          <div className="summary-item">
            <span className="summary-value">{isMLPMode ? players.length : expectedPlayers} players</span>
            <span className="summary-label">Player Count:</span>
          </div>
          <div className="summary-item">
            <span className="summary-value">{waitingCount} waiting</span>
            <span className="summary-label">Players on Bench:</span>
          </div>
          <div className="summary-item">
            <span className="summary-value">{gameDuration} minutes</span>
            <span className="summary-label">Game Duration:</span>
          </div>
          <div className="summary-item">
            <span className="summary-value">{isMLPMode ? 'tournament' : competitionLevel}</span>
            <span className="summary-label">Competition Level:</span>
          </div>
        </div>

        <div className="session-description">
          {isMLPMode 
            ? "Dynamic MLP Tournament: Adaptive wait distribution (4C+5 to 8C), partnership optimization, DINKR V2.0 ratings"
            : "Dynamic SMART Rotation: Adaptive wait distribution (4C+5 to 8C), partnership optimization, DINKR V2.0 ratings"
          }
        </div>
      </div>

      {/* Player Selection for SMART mode */}
      {!isMLPMode && players && players.length > 0 && (
        <div className="player-selection">
          <div className="selection-header">
            <h3>Select Players ({selectedPlayers ? selectedPlayers.length : 0} selected)</h3>
            <div className="selection-actions">
              <button onClick={handleSelectAll} className="select-button">
                Select All
              </button>
              <button onClick={handleSelectNone} className="select-button">
                Select None
              </button>
            </div>
          </div>

          <div className="players-grid">
            {players.map(player => (
              <div
                key={player.id}
                className={`player-card ${selectedPlayers && selectedPlayers.find(p => p.id === player.id) ? 'selected' : ''}`}
                onClick={() => handlePlayerToggle(player)}
              >
                <div className="player-name">{player.name}</div>
                <div className="player-rating">Rating: {player.rating ? player.rating.toFixed(1) : '3.5'}</div>
                <div className="player-games">Games: {player.totalGames || 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Start Session */}
      <div className="start-session">
        <button
          className="start-button"
          onClick={handleStartSession}
          disabled={!hasEnoughPlayers}
        >
          <Play size={20} />
          {isMLPMode 
            ? `Setup MLP Tournament (${players.length} players)` 
            : `Start Session (${selectedPlayers.length} players)`
          }
        </button>
        
        {!hasEnoughPlayers && (
          <p className="min-players-note">
            {isMLPMode 
              ? `Need exactly 16 players for MLP tournament (currently have ${players.length})`
              : `Need at least 8 selected players to start session (currently have ${selectedPlayers.length})`
            }
          </p>
        )}
      </div>
    </div>
  );
};

export default PlayerSetup;